import React, { useEffect, useState } from 'react';
import { Image } from './image';
import TagManager from 'react-gtm-module';
import { OnOffSwitch } from '../OnOffSwitch';
import { motion } from 'framer-motion';

import style from './interactive-track.module.scss';
import { StyledSelect } from '../StyledSelect';
import { Button } from '../ButtonBar/button';

let iwdTimeout = undefined;

const InteractiveTrack = ({ config }) => {
  const [selected, setSelected] = useState();
  const [options, setOptions] = useState();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(true);
  const [activeJson, setActiveJson] = useState({});

  const fetchActive = async (active) => {
    if (!config.activeJsonUri) {
      return false;
    }

    clearTimeout(iwdTimeout);
    iwdTimeout = undefined;

    const json = await fetch(config.activeJsonUri, { credentials: 'include' })
      .then((data) => data.json())
      .then((response) => {
        return response;
      });
    setActiveJson(json);

    if (config.useActiveToggle && active && json.status !== 'finished') {
      iwdTimeout = setTimeout(
        () => fetchActive(active),
        config.pollRateSeconds ? config.pollRateSeconds * 1000 : 15000
      );
    }
  };

  const changeActiveState = (newState) => {
    clearTimeout(iwdTimeout);
    iwdTimeout = undefined;
    setActive(newState);
  };

  useEffect(() => {
    if (!active) {
      clearTimeout(iwdTimeout);
      iwdTimeout = undefined;
    } else {
      fetchActive(active);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => {
    if (activeJson.active && options && active) {
      const current = options.find(({ label }) => label === activeJson.active[0]);

      setSelected(current);
    }
  }, [activeJson, options, active]);

  useEffect(() => {
    fetch(config.jsonUri, { credentials: 'include' })
      .then((data) => data.json())
      .then((response) => {
        // support nested items
        response = response.hasOwnProperty('interactive_track')
          ? response.interactive_track.slides
          : response;
        const values = response.map((data, index) => {
          if (!data.hasOwnProperty('value')) {
            data.value = `iwdd-${index}`;
          }

          return data;
        });

        setOptions(values);
        setSelected(values[0]);
      });

    if (iwdTimeout) {
      clearTimeout(iwdTimeout);
      iwdTimeout = undefined;
    }

    if (config.useActiveToggle) {
      fetchActive(active);
    }

    const dataLayerArgs = {
      dataLayer: {
        event: 'rbVideoTracking',
        eventCategory: 'video',
        eventAction: 'sidebar',
        eventLabel: 'loaded',
        widgetType: `InteractiveTrack - ${
          config.trackingTitle || config.navigationTitle
        }`
      }
    };

    TagManager.dataLayer(dataLayerArgs);

    return () => {
      clearTimeout(iwdTimeout);
      iwdTimeout = undefined;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <motion.div className="appRow InteractiveTrack" key={config.navigationTitle}>
        {options && options.length && (
          <div className="appRow full-width">
            <div className={style.selectboxWrapper} style={config.dropdownPosition || {}}>
              <StyledSelect
                className={`mini${config.hideDropdown === true ? ' hidden' : ''}`}
                value={{
                  label:
                    config.selectText || { label: 'Change Athlete', ...selected }.label,
                  value: selected
                    ? selected.value
                    : options
                      ? setSelected(options[0]) || options[0].value
                      : undefined
                }}
                options={options}
                selectFirst={true}
                open={open}
                useCapitalize={!config.leaveNamesCapitalization}
                onOpen={() => {
                  setOpen(!open);
                }}
                onChange={(value) => {
                  const dataLayerArgs = {
                    dataLayer: {
                      event: 'rbVideoTracking',
                      eventCategory: 'video',
                      eventAction: 'sidebar',
                      eventLabel: 'Dropdown - Image withDropdown',
                      widgetType: `InteractiveTrack - ${config.trackingTitle || config.navigationTitle
                        }`,
                      widgetTypeProfile: `${value.label}`
                    }
                  };

                  TagManager.dataLayer(dataLayerArgs);

                  setActive(false);
                  setSelected(value);
                }}
                maxHeight={400}
              />
            </div>
            {config.useActiveToggle && (
              <div
                className={style.activeToggleWrapper}
                style={config.activeTogglePosition || {}}
                onClick={() => {
                  changeActiveState(!active);
                  const dataLayerArgs = {
                    dataLayer: {
                      event: 'rbVideoTracking',
                      eventCategory: 'video',
                      eventAction: 'sidebar',
                      eventLabel: `Auto Update - ${!active ? 'ON' : 'OFF'}`,
                      widgetType: `InteractiveTrack - ${config.trackingTitle || config.navigationTitle
                        }`,
                      widgetTypeProfile: `${selected.label}`
                    }
                  };

                  TagManager.dataLayer(dataLayerArgs);
                }}
              >
                {!active && <>Auto Update off</>} <OnOffSwitch on={active} />
              </div>
            )}
            <Image
              src={{ image: '', ...selected }.image}
              alt={{ label: '', ...selected }.label}
              options={options}
              selected={selected}
              setSelected={setSelected}
              textboxes={{ textboxes: '', ...selected }.textboxes}
              config={config}
              setAutopoll={changeActiveState}
            />
          </div>
        )}
      </motion.div>
      {config.buttons &&
        Array.isArray(config.buttons) &&
        config.buttons.length &&
        config.buttons.map((button) => (
          <Button
            key={button.text}
            {...button}
            position={{ ...button.position, position: 'absolute', zIndex: 1000 }}
            onClick={() => {
              const dataLayerArgs = {
                dataLayer: {
                  event: 'rbVideoTracking',
                  eventCategory: 'video',
                  eventAction: 'sidebar',
                  eventLabel: `Button – Global - ${button.text}`,
                  widgetType: `InteractiveTrack - ${
                    config.trackingTitle || config.navigationTitle
                  }`
                }
              };

              TagManager.dataLayer(dataLayerArgs);
            }}>
            {button.text}
          </Button>
        ))
      }
    </>
  );
};

export { InteractiveTrack };
